import React, { forwardRef, useState, useCallback } from "react";
import clsx from "clsx";

// Base Slider Component
interface SliderProps {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "success" | "error";
  color?: "blue" | "green" | "yellow" | "red" | "purple" | "orange";
  discrete?: boolean;
  marks?: Array<{ value: number; label?: string }>;
  className?: string;
  labelClassName?: string;
  id?: string;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      value,
      defaultValue = 0,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      label,
      helperText,
      error,
      disabled = false,
      size = "md",
      variant = "default",
      color = "blue",
      discrete = false,
      marks = [],
      className,
      labelClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value ?? defaultValue);
    const [isHovered, setIsHovered] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const sliderId = id || `slider-${Math.random().toString(36).substr(2, 9)}`;

    const sizeStyles = {
      sm: {
        track: "h-1.5",
        thumb: "w-3 h-3",
        thumbOffset: "top-1/2 -translate-y-1/2 -translate-x-1/2",
      },
      md: {
        track: "h-2",
        thumb: "w-4 h-4",
        thumbOffset: "top-1/2 -translate-y-1/2 -translate-x-1/2",
      },
      lg: {
        track: "h-3",
        thumb: "w-5 h-5",
        thumbOffset: "top-1/2 -translate-y-1/2 -translate-x-1/2",
      },
    };

    const colorStyles = {
      blue: "bg-tracq-bright-blue",
      green: "bg-green-500",
      yellow: "bg-yellow-500",
      red: "bg-red-500",
      purple: "bg-purple-500",
      orange: "bg-tracq-deep-orange",
    };

    const variantStyles = {
      default: {
        track: "bg-gray-700",
        range: colorStyles[color],
        thumb: "bg-white border-gray-700",
        thumbHover: "ring-tracq-bright-blue",
        thumbActive: "bg-tracq-bright-blue border-tracq-bright-blue",
      },
      success: {
        track: "bg-gray-700",
        range: "bg-green-500",
        thumb: "bg-white border-gray-700",
        thumbHover: "ring-green-500",
        thumbActive: "bg-green-500 border-green-500",
      },
      error: {
        track: "bg-gray-700",
        range: "bg-red-500",
        thumb: "bg-white border-gray-700",
        thumbHover: "ring-red-500",
        thumbActive: "bg-red-500 border-red-500",
      },
    };

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(e.target.value);
        setInternalValue(newValue);
        onChange?.(newValue);
      },
      [onChange],
    );

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    const handleMouseDown = () => setIsActive(true);
    const handleMouseUp = () => setIsActive(false);

    const currentValue = value ?? internalValue;
    const percentage = ((currentValue - min) / (max - min)) * 100;
    const currentSize = sizeStyles[size];
    const currentVariant = variantStyles[variant];
    const hasError = !!error;

    const trackClasses = clsx(
      "relative rounded-full cursor-pointer",
      currentSize.track,
      currentVariant.track,
      {
        "cursor-not-allowed opacity-50": disabled,
        "bg-red-600": hasError && !disabled,
      },
    );

    const rangeClasses = clsx(
      "absolute top-0 left-0 h-full rounded-full transition-all duration-200",
      currentVariant.range,
      {
        "bg-red-500": hasError && !disabled,
      },
    );

    const thumbClasses = clsx(
      "absolute rounded-full border-2 shadow-lg transition-all duration-200 cursor-pointer",
      currentSize.thumb,
      currentSize.thumbOffset,
      currentVariant.thumb,
      {
        [currentVariant.thumbHover]: isHovered && !disabled,
        [currentVariant.thumbActive]: isActive && !disabled,
        "cursor-not-allowed": disabled,
        "bg-gray-400 border-gray-500": disabled,
      },
    );

    return (
      <div className={clsx("flex flex-col", className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={sliderId}
            className={clsx(
              "block text-sm font-medium text-gray-300 mb-2",
              disabled && "text-gray-500",
              labelClassName,
            )}
          >
            {label}
          </label>
        )}

        {/* Slider Container */}
        <div className="relative">
          {/* Track */}
          <div className={trackClasses}>
            {/* Range (filled portion) */}
            <div
              className={rangeClasses}
              style={{ width: `${percentage}%`, pointerEvents: "none" }}
            />

            {/* Thumb */}
            <div
              className={thumbClasses}
              style={{ left: `${percentage}%`, pointerEvents: "none" }}
            />
          </div>

          {/* Interactive Input - Overlay on track for clickability */}
          <input
            ref={ref}
            type="range"
            id={sliderId}
            min={min}
            max={max}
            step={step}
            value={currentValue}
            onChange={handleChange}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            disabled={disabled}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            style={{
              WebkitAppearance: "none",
              appearance: "none",
              background: "transparent",
              pointerEvents: "auto",
            }}
            {...props}
          />

          {/* Marks for discrete slider */}
          {discrete && marks.length > 0 && (
            <div className="flex justify-between mt-2">
              {marks.map((mark, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center"
                  style={{
                    left: `${((mark.value - min) / (max - min)) * 100}%`,
                  }}
                >
                  <div className="w-1 h-1 bg-gray-500 rounded-full mb-1" />
                  {mark.label && <span className="text-xs text-gray-400">{mark.label}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Value Display */}
        <div className="flex justify-between items-center mt-2">
          <span className="text-sm text-gray-400">{min}</span>
          <span className="text-sm font-medium text-gray-200">{currentValue}</span>
          <span className="text-sm text-gray-400">{max}</span>
        </div>

        {/* Helper/Error text */}
        {(helperText || error) && (
          <div className={clsx("mt-2 text-sm", error ? "text-red-400" : "text-gray-400")}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  },
);

Slider.displayName = "Slider";

// Range Slider Component (dual thumb)
interface RangeSliderProps {
  value?: [number, number];
  defaultValue?: [number, number];
  onChange?: (value: [number, number]) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "success" | "error";
  color?: "blue" | "green" | "yellow" | "red" | "purple" | "orange";
  className?: string;
  labelClassName?: string;
  id?: string;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  value,
  defaultValue = [0, 100],
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  helperText,
  error,
  disabled = false,
  size = "md",
  variant = "default",
  color = "blue",
  className,
  labelClassName,
  id,
}) => {
  const [internalValue, setInternalValue] = useState<[number, number]>(value ?? defaultValue);
  const sliderId = id || `range-slider-${Math.random().toString(36).substr(2, 9)}`;

  const sizeStyles = {
    sm: {
      track: "h-1.5",
      thumb: "w-3 h-3",
      thumbOffset: "top-1/2 -translate-y-1/2 -translate-x-1/2",
    },
    md: {
      track: "h-2",
      thumb: "w-4 h-4",
      thumbOffset: "top-1/2 -translate-y-1/2 -translate-x-1/2",
    },
    lg: {
      track: "h-3",
      thumb: "w-5 h-5",
      thumbOffset: "top-1/2 -translate-y-1/2 -translate-x-1/2",
    },
  };

  const colorStyles = {
    blue: "bg-tracq-bright-blue",
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    red: "bg-red-500",
    purple: "bg-purple-500",
    orange: "bg-tracq-deep-orange",
  };

  const variantStyles = {
    default: {
      track: "bg-gray-700",
      range: colorStyles[color],
      thumb: "bg-white border-gray-700",
      thumbHover: "ring-tracq-bright-blue",
      thumbActive: "bg-tracq-bright-blue border-tracq-bright-blue",
    },
    success: {
      track: "bg-gray-700",
      range: "bg-green-500",
      thumb: "bg-white border-gray-700",
      thumbHover: "ring-green-500",
      thumbActive: "bg-green-500 border-green-500",
    },
    error: {
      track: "bg-gray-700",
      range: "bg-red-500",
      thumb: "bg-white border-gray-700",
      thumbHover: "ring-red-500",
      thumbActive: "bg-red-500 border-red-500",
    },
  };

  const handleMinChange = (newMin: number) => {
    const newValue: [number, number] = [newMin, internalValue[1]];
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  const handleMaxChange = (newMax: number) => {
    const newValue: [number, number] = [internalValue[0], newMax];
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  const currentValue = value ?? internalValue;
  const minPercentage = ((currentValue[0] - min) / (max - min)) * 100;
  const maxPercentage = ((currentValue[1] - min) / (max - min)) * 100;
  const currentSize = sizeStyles[size];
  const currentVariant = variantStyles[variant];
  const hasError = !!error;

  const trackClasses = clsx(
    "relative rounded-full cursor-pointer",
    currentSize.track,
    currentVariant.track,
    {
      "cursor-not-allowed opacity-50": disabled,
      "bg-red-600": hasError && !disabled,
    },
  );

  const rangeClasses = clsx(
    "absolute top-0 h-full rounded-full transition-all duration-200",
    currentVariant.range,
    {
      "bg-red-500": hasError && !disabled,
    },
  );

  const thumbClasses = clsx(
    "absolute rounded-full border-2 shadow-lg transition-all duration-200 cursor-pointer",
    currentSize.thumb,
    currentSize.thumbOffset,
    currentVariant.thumb,
    {
      "cursor-not-allowed": disabled,
      "bg-gray-400 border-gray-500": disabled,
    },
  );

  return (
    <div className={clsx("flex flex-col", className)}>
      {/* Label */}
      {label && (
        <label
          htmlFor={sliderId}
          className={clsx(
            "block text-sm font-medium text-gray-300 mb-2",
            disabled && "text-gray-500",
            labelClassName,
          )}
        >
          {label}
        </label>
      )}

      {/* Slider Container */}
      <div className="relative">
        {/* Track */}
        <div className={trackClasses}>
          {/* Range (filled portion between thumbs) */}
          <div
            className={rangeClasses}
            style={{
              left: `${minPercentage}%`,
              width: `${maxPercentage - minPercentage}%`,
            }}
          />

          {/* Min Thumb */}
          <div className={thumbClasses} style={{ left: `${minPercentage}%` }} />

          {/* Max Thumb */}
          <div className={thumbClasses} style={{ left: `${maxPercentage}%` }} />
        </div>

        {/* Hidden Inputs */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue[0]}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          disabled={disabled}
          className="sr-only"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue[1]}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          disabled={disabled}
          className="sr-only"
        />
      </div>

      {/* Value Display */}
      <div className="flex justify-between items-center mt-2">
        <span className="text-sm text-gray-400">{min}</span>
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-200">{currentValue[0]}</span>
          <span className="text-sm text-gray-400">-</span>
          <span className="text-sm font-medium text-gray-200">{currentValue[1]}</span>
        </div>
        <span className="text-sm text-gray-400">{max}</span>
      </div>

      {/* Helper/Error text */}
      {(helperText || error) && (
        <div className={clsx("mt-2 text-sm", error ? "text-red-400" : "text-gray-400")}>
          {error || helperText}
        </div>
      )}
    </div>
  );
};

// Discrete Slider Component
interface DiscreteSliderProps extends Omit<SliderProps, "discrete"> {
  steps: number;
  stepLabels?: string[];
}

export const DiscreteSlider: React.FC<DiscreteSliderProps> = ({
  steps,
  stepLabels = [],
  ...props
}) => {
  const marks = Array.from({ length: steps + 1 }, (_, i) => ({
    value: (props.max ?? 100) * (i / steps),
    label: stepLabels[i] || `${i}`,
  }));

  return <Slider {...props} discrete marks={marks} step={(props.max ?? 100) / steps} />;
};

// Colored Slider Component
interface ColoredSliderProps extends Omit<SliderProps, "variant"> {
  color: "blue" | "green" | "yellow" | "red" | "purple" | "orange";
}

export const ColoredSlider: React.FC<ColoredSliderProps> = ({ color, ...props }) => {
  return <Slider {...props} color={color} />;
};
